import { useCallback, useEffect, useRef } from 'react';

import { expVal } from '@confluence/feature-experiments';
import { getLogger } from '@confluence/logger';
import { fetchCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';
import { normalizeCommentData } from '@confluence/comments-data/entry-points/commentsDataHelper.ts';
import {
	CommentType,
	useCommentsData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type {
	CommentData,
	CommentsDataMap,
	ReplyData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type { ReactionsSummary } from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';

const logger = getLogger('useSyncLegacyInlineReactionCommentsData');

type ActionType = 'add' | 'delete';
type LegacyInlineReactionThreadComment = Parameters<typeof normalizeCommentData>[0]['comment'];

export type SyncLegacyCommentsDataReactionsSummaryParams = {
	commentId: string;
	markerRef: string;
	emojiId: string;
	actionType: ActionType;
};

export type SyncLegacyCommentsDataReactionThreadParams = {
	commentId: string;
	markerRef: string;
};

export type UseSyncLegacyInlineReactionCommentsDataOptions = {
	contentId: string;
	currentUserId: string | null;
};

const updateRemoteReactionsForComment = <T extends CommentData | ReplyData>(
	commentData: T | undefined,
	emojiId: string,
	actionType: ActionType,
): T | undefined => {
	if (!commentData) {
		return undefined;
	}

	const { reactionsSummary } = commentData;
	if (!reactionsSummary) {
		return undefined;
	}

	const reactionsAri = reactionsSummary.ari || '';
	const reactionsContainerAri = reactionsSummary.containerAri || '';
	const reactionsNodes = reactionsSummary.reactionsSummaryForEmoji || [];
	let updatedReactionsNodes = reactionsNodes.map((item) => (item ? { ...item } : item));
	const reactionIdx = updatedReactionsNodes.findIndex((item) => item?.emojiId === emojiId);

	if (actionType === 'add') {
		if (reactionIdx !== -1 && updatedReactionsNodes[reactionIdx]) {
			const reaction = updatedReactionsNodes[reactionIdx];
			updatedReactionsNodes[reactionIdx] = {
				...reaction,
				count: reaction.count + 1,
				reacted: reaction.reacted,
			};
		} else {
			updatedReactionsNodes.push({
				emojiId,
				count: 1,
				reacted: false,
				id: `${reactionsContainerAri}|${reactionsAri}|${emojiId}`,
			});
		}
	} else {
		if (reactionIdx === -1 || !updatedReactionsNodes[reactionIdx]) {
			return undefined;
		}
		const reaction = updatedReactionsNodes[reactionIdx];
		const nextCount = reaction.count - 1;

		if (nextCount <= 0 && !reaction.reacted) {
			updatedReactionsNodes = [
				...updatedReactionsNodes.slice(0, reactionIdx),
				...updatedReactionsNodes.slice(reactionIdx + 1),
			];
		} else {
			updatedReactionsNodes[reactionIdx] = {
				...reaction,
				count: reaction.reacted ? Math.max(1, nextCount) : nextCount,
				reacted: reaction.reacted,
			};
		}
	}

	return {
		...commentData,
		reactionsSummary: {
			...reactionsSummary,
			reactionsSummaryForEmoji: updatedReactionsNodes,
			reactionsCount: updatedReactionsNodes.reduce(
				(total, reaction) => total + (reaction?.count || 0),
				0,
			),
		} as ReactionsSummary,
	};
};

const updateCommentThreadReactions = ({
	comment,
	commentId,
	emojiId,
	actionType,
}: {
	comment: CommentData;
	commentId: string;
	emojiId: string;
	actionType: ActionType;
}): CommentData | undefined => {
	if (comment.id === commentId) {
		return updateRemoteReactionsForComment(comment, emojiId, actionType);
	}

	let updatedReply: ReplyData | undefined;
	const updatedReplies = comment.replies.map((reply) => {
		if (reply.id !== commentId) {
			return reply;
		}
		updatedReply = updateRemoteReactionsForComment(reply, emojiId, actionType);
		return updatedReply ?? reply;
	});

	if (!updatedReply) {
		return undefined;
	}

	return {
		...comment,
		replies: updatedReplies,
	};
};

export const useSyncLegacyInlineReactionCommentsData = ({
	contentId,
	currentUserId,
}: UseSyncLegacyInlineReactionCommentsDataOptions): {
	syncLegacyCommentsDataReactionsSummary: ({
		commentId,
		markerRef,
		emojiId,
		actionType,
	}: SyncLegacyCommentsDataReactionsSummaryParams) => void;
	syncLegacyCommentsDataReactionThread: ({
		commentId,
		markerRef,
	}: SyncLegacyCommentsDataReactionThreadParams) => Promise<void>;
} => {
	const [{ commentsDataMap }, { addNewCommentThreads, setCommentsDataMap }] = useCommentsData();
	const commentsDataMapRef = useRef(commentsDataMap);

	useEffect(() => {
		commentsDataMapRef.current = commentsDataMap;
	}, [commentsDataMap]);

	const syncLegacyCommentsDataReactionsSummary = useCallback(
		({
			commentId,
			markerRef,
			emojiId,
			actionType,
		}: SyncLegacyCommentsDataReactionsSummaryParams): void => {
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
				return;
			}

			const latestCommentsDataMap = commentsDataMapRef.current;
			const comment = latestCommentsDataMap.inline[markerRef];
			if (!comment) {
				return;
			}

			const updatedComment = updateCommentThreadReactions({
				comment,
				commentId,
				emojiId,
				actionType,
			});
			if (!updatedComment) {
				return;
			}

			setCommentsDataMap({
				inline: {
					...latestCommentsDataMap.inline,
					[markerRef]: updatedComment,
				},
				general: {
					...latestCommentsDataMap.general,
				},
			});
		},
		[setCommentsDataMap],
	);

	const syncLegacyCommentsDataReactionThread = useCallback(
		async ({ commentId, markerRef }: SyncLegacyCommentsDataReactionThreadParams): Promise<void> => {
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
				return;
			}

			const fetchedComment = await fetchCommentById({
				commentId,
				contentId,
				fetchPolicy: 'network-only',
			}).catch((error: unknown) => {
				logger.warn`Failed to fetch comment for legacy inline reaction thread sync: ${
					error instanceof Error ? error : new Error(String(error))
				}`;
				return null;
			});

			if (!fetchedComment) {
				return;
			}

			const newCommentThreads: CommentsDataMap = {
				inline: {
					[markerRef]: normalizeCommentData({
						comment: fetchedComment as LegacyInlineReactionThreadComment,
						currentUserId: currentUserId ?? '',
						isOpen: true,
						type: CommentType.INLINE,
					}),
				},
				general: {},
			};

			addNewCommentThreads(newCommentThreads);
		},
		[addNewCommentThreads, contentId, currentUserId],
	);

	return {
		syncLegacyCommentsDataReactionsSummary,
		syncLegacyCommentsDataReactionThread,
	};
};
